import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import dayjs from 'dayjs';
import { t, tKey } from 'ember-intl';
import importAsset from 'lumen/helpers/import-asset';
import type Highlight from 'lumen/models/roi-trophies/highlight';
import type { ReservationPricePushData } from 'lumen/models/roi-trophies/highlight';
import lottieAnimation from 'lumen/modifiers/lottie-animation';

import GeneralRoiTrophiesCountUpNumber from '../count-up-number';
import GeneralRoiTrophiesContentTrophyModalLayout from './trophy-modal-layout';

// Follow-up sentences appended after the main body copy.
// Rotated using Math.random() to add variety across repeated trophy views.
// The modal is triggered at most once per week and cannot be manually reopened,
// so re-randomising on each render is a non-issue in practice.
const FOLLOW_UP_KEYS = {
  increase: [
    // Intentional break from the `reservation_price_push` prefix — this key is shared with the revenue price push trophy modal.
    tKey('roi_trophies.price_push.follow_up_increase_1'),
    tKey('roi_trophies.reservation_price_push.follow_up_increase_2'),
    tKey('roi_trophies.reservation_price_push.follow_up_increase_3'),
  ],
  decrease: [
    tKey('roi_trophies.reservation_price_push.follow_up_decrease_1'),
    tKey('roi_trophies.reservation_price_push.follow_up_decrease_2'),
    tKey('roi_trophies.reservation_price_push.follow_up_decrease_3'),
  ],
} as const;

interface GeneralRoiTrophiesContentReservationPricePushSignature {
  Args: {
    highlight: Highlight;
  };
}

export default class GeneralRoiTrophiesContentReservationPricePush extends Component<GeneralRoiTrophiesContentReservationPricePushSignature> {
  @service declare dates: Services['dates'];

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];

  private followUpKey: (typeof FOLLOW_UP_KEYS)[keyof typeof FOLLOW_UP_KEYS][number];

  get body(): string {
    const bodyKey = this.isPriceIncrease
      ? tKey('roi_trophies.reservation_price_push.body_increase')
      : tKey('roi_trophies.reservation_price_push.body_decrease');

    const main = this.intl.t(bodyKey, {
      priceChange: this.formatCurrency(this.priceChangeAbs),
    });

    return `${main} ${this.followUp}`;
  }

  get currency(): string {
    return this.hotelAndMarketService.hotel!.currencyCode;
  }

  get data(): ReservationPricePushData {
    return this.args.highlight.highlightData as ReservationPricePushData;
  }

  private get followUp(): string {
    return this.intl.t(this.followUpKey);
  }

  get formattedDate(): string {
    const date = this.args.highlight.periodStart!;

    return dayjs(date).format(
      this.dates.partialDateFormat.WEEKDAY_AND_YEAR_MONTH_AND_DAY_SHORT,
    );
  }

  get isPriceIncrease(): boolean {
    return this.data.price_change > 0;
  }

  get priceChangeAbs(): number {
    return Math.abs(this.data.price_change);
  }

  constructor(
    owner: Owner,
    args: GeneralRoiTrophiesContentReservationPricePushSignature['Args'],
  ) {
    super(owner, args);
    const isPriceIncrease =
      (args.highlight.highlightData as ReservationPricePushData).price_change >
      0;
    const keys = FOLLOW_UP_KEYS[isPriceIncrease ? 'increase' : 'decrease'];
    this.followUpKey = keys[Math.floor(Math.random() * keys.length)]!;
  }

  private formatCurrency(value: number): string {
    return this.intl.formatNumber(value, {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  <template>
    <GeneralRoiTrophiesContentTrophyModalLayout>
      <:content>
        <div class="flex flex-col items-center gap-300">
          <div>
            <div class="heading-1">
              <GeneralRoiTrophiesCountUpNumber
                @prefix="+ "
                @target={{this.data.pickup}}
              />
              {{t "roi_trophies.reservations"}}
            </div>
            <p>{{t
                "roi_trophies.reservation.subline"
                date=this.formattedDate
              }}</p>
          </div>
          <p
            class="text-300--regular text-neutral-subdued"
            data-test-id="reservation-price-push-body"
          >{{this.body}}</p>
        </div>
      </:content>
      <:animation>
        <div
          {{lottieAnimation
            (importAsset "animations/roi-trophies/record.json")
            false
          }}
        />
      </:animation>
    </GeneralRoiTrophiesContentTrophyModalLayout>
  </template>
}
